import React from 'react'
import classNames from 'classnames'
import {
    WemoloButton,
    WemoloCheckbox,
    WemoloEyeOpenIcon,
    WemoloInfoIcon,
    WemoloLicensePlate,
    WemoloScannerIcon,
    WemoloTypography
} from '../../../components/Wemolo'
import {
    DETECTION_EXPLORER_COLUMNS,
    DetectionExplorerColumn,
    DETECTION_EXPLORER_TEXT,
    isFieldUnset,
    resolveKnownCountry
} from '../config'
import { InfoTooltip } from '../../../components/InfoTooltip'
import { formatDetectionTimestamp } from '../formatTimestamp'
import { DetectionsEmptyState } from '../EmptyState'
import { CategoryBadge, DirectionChip } from '../Gallery'
import { DetectionImage, DetectionResult } from '../get_detections'
import styles from './Table.module.css'

const formatConf = (value: number | null) => (value == null ? DETECTION_EXPLORER_TEXT.emptyValue : value.toFixed(2))
const formatText = (value: string | null) =>
    value == null ? DETECTION_EXPLORER_TEXT.emptyValue : isFieldUnset(value) ? DETECTION_EXPLORER_TEXT.notSet : value

// One badge per image artifact. Three states: green when the signed URL is present, yellow when
// it has been requested but not yet uploaded, grey when it's on-device and unrequested. Order
// mirrors the detail modal tabs.
const MEDIA_BADGES: ReadonlyArray<{
    label: string
    title: string
    get: (d: DetectionResult) => DetectionImage
}> = [
    { label: 'R', title: 'Detection render', get: (d) => d.images.render },
    { label: 'C', title: 'Raw (region of interest)', get: (d) => d.images.roi },
    { label: 'P', title: 'License plate crop', get: (d) => d.images.lp }
]

type MediaBadgeState = 'present' | 'requested' | 'absent'

const mediaBadgeState = (image: DetectionImage): MediaBadgeState =>
    image.url ? 'present' : image.requested ? 'requested' : 'absent'

const MEDIA_STATE_CLASS: Record<MediaBadgeState, string> = {
    present: styles.mediaBadgePresent,
    requested: styles.mediaBadgeRequested,
    absent: styles.mediaBadgeAbsent
}

const MEDIA_STATE_SUFFIX: Record<MediaBadgeState, string> = {
    present: '',
    requested: ` — ${DETECTION_EXPLORER_TEXT.requested}`,
    absent: ` — ${DETECTION_EXPLORER_TEXT.onDevice}`
}

const MediaBadges: React.FC<{ detection: DetectionResult }> = ({ detection }) => (
    <div className={styles.mediaBadges}>
        {MEDIA_BADGES.map(({ label, title, get }) => {
            const state = mediaBadgeState(get(detection))
            return (
                <InfoTooltip key={label} content={`${title}${MEDIA_STATE_SUFFIX[state]}`}>
                    <span className={classNames(styles.mediaBadge, MEDIA_STATE_CLASS[state])}>
                        <WemoloTypography variant="label-small" tag="span">
                            {label}
                        </WemoloTypography>
                    </span>
                </InfoTooltip>
            )
        })}
    </div>
)

const MediaHeaderLabel: React.FC<{ label: string }> = ({ label }) => (
    <span className={styles.mediaHeader}>
        <WemoloTypography variant="label-xsmall" tag="span">
            {label}
        </WemoloTypography>
        <WemoloInfoIcon
            className={styles.mediaHeaderInfo}
            tabIndex={0}
            role="img"
            aria-label={DETECTION_EXPLORER_TEXT.media.legendLabel}
        />
        <span role="tooltip" className={styles.mediaTooltip}>
            <WemoloTypography variant="label-small" tag="span">
                {DETECTION_EXPLORER_TEXT.media.legend}
            </WemoloTypography>
        </span>
    </span>
)

export type TableUIProps = {
    items: DetectionResult[]
    selectedIds: string[]
    onToggleSelect: (id: string) => void
    onOpenDetail: (id: string) => void
    onFilterDevice: (deviceId: number) => void
    columns?: DetectionExplorerColumn[]
}

export const TableUI: React.FC<TableUIProps> = ({
    items,
    selectedIds,
    onToggleSelect,
    onOpenDetail,
    onFilterDevice,
    columns = DETECTION_EXPLORER_COLUMNS
}) => (
    <div className={styles.tableWrapper}>
        <table className={styles.table}>
            <thead>
                <tr className={styles.headerRow}>
                    <th className={`${styles.headerCell} ${styles.checkboxCell}`} aria-hidden />
                    {columns.map((col) => (
                        <th key={col.key} className={styles.headerCell}>
                            {col.key === 'media' ? (
                                <MediaHeaderLabel label={col.label} />
                            ) : (
                                <WemoloTypography variant="label-xsmall" tag="span">
                                    {col.label}
                                </WemoloTypography>
                            )}
                        </th>
                    ))}
                    <th className={`${styles.headerCell} ${styles.actionsCell}`}>
                        <WemoloTypography variant="label-xsmall" tag="span">
                            {DETECTION_EXPLORER_TEXT.table.actions}
                        </WemoloTypography>
                    </th>
                </tr>
            </thead>
            <tbody>
                {items.length === 0 ? (
                    <tr>
                        <td colSpan={columns.length + 2}>
                            <DetectionsEmptyState />
                        </td>
                    </tr>
                ) : (
                    items.map((d) => {
                        const isSelected = selectedIds.includes(d.detection_uuid)
                        return (
                            <tr key={d.detection_uuid} className={styles.row}>
                                <td className={`${styles.cell} ${styles.checkboxCell}`}>
                                    <div className={styles.checkboxWrap}>
                                        <WemoloCheckbox
                                            checked={isSelected}
                                            onChange={() => onToggleSelect(d.detection_uuid)}
                                        />
                                    </div>
                                </td>
                                <td className={styles.cell}>
                                    <WemoloTypography variant="body-small" tag="span">
                                        {formatDetectionTimestamp(d.timestamp)}
                                    </WemoloTypography>
                                </td>
                                <td className={styles.cell}>
                                    <WemoloTypography variant="body-small" tag="span">
                                        {formatText(d.country)}
                                    </WemoloTypography>
                                </td>
                                <td className={styles.cell}>
                                    {d.license_plate ? (
                                        <WemoloLicensePlate
                                            plateNumber={d.license_plate}
                                            size="small"
                                            country={resolveKnownCountry(d.country)}
                                            className={styles.plate}
                                        />
                                    ) : (
                                        <WemoloTypography variant="body-small" tag="span">
                                            {DETECTION_EXPLORER_TEXT.emptyValue}
                                        </WemoloTypography>
                                    )}
                                </td>
                                <td className={styles.cell}>
                                    <button
                                        type="button"
                                        className={styles.deviceBadge}
                                        title={d.device_uuid}
                                        aria-label={`Filter by device ${d.device_id}`}
                                        onClick={() => onFilterDevice(d.device_id)}
                                    >
                                        <WemoloScannerIcon className={styles.deviceBadgeIcon} />
                                        <WemoloTypography variant="label-small" tag="span">
                                            {d.device_id}
                                        </WemoloTypography>
                                    </button>
                                </td>
                                <td className={styles.cell}>
                                    <DirectionChip direction={d.direction} />
                                </td>
                                <td className={styles.cell}>
                                    <CategoryBadge category={d.category} />
                                </td>
                                <td className={styles.cell}>
                                    <WemoloTypography variant="body-small" tag="span">
                                        {formatConf(d.lpr_conf)}
                                    </WemoloTypography>
                                </td>
                                <td className={styles.cell}>
                                    <MediaBadges detection={d} />
                                </td>
                                <td className={`${styles.cell} ${styles.actionsCell}`}>
                                    <WemoloButton
                                        view="ghost"
                                        size="small"
                                        icon={<WemoloEyeOpenIcon />}
                                        aria-label={DETECTION_EXPLORER_TEXT.viewFullSize}
                                        onClick={() => onOpenDetail(d.detection_uuid)}
                                    />
                                </td>
                            </tr>
                        )
                    })
                )}
            </tbody>
        </table>
    </div>
)
